import Alert from 'components/UI/Alert'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import { Checkbox } from 'components/UI/Checkbox'
import Typography from 'components/UI/Typography'
import { useAppSelector } from 'hooks'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import OtpInput from 'react-otp-input'
import { useAttachBankManagerMutation, useLazyGetBankManagersQuery } from 'redux/api/creditApi'
import { getActiveCreditApplication } from 'redux/store/slices/userSlice'
import tw, { TwStyle } from 'twin.macro'

const NUM_INPUTS = 4

export type BankManagerCodeProps = {
  twStyle?: TwStyle
  isLoading?: boolean
  onAttached?: () => void
}

const BankManagerCode = ({ twStyle, isLoading, onAttached }: BankManagerCodeProps) => {
  const { t } = useTranslation()
  const { id: creditID } = useAppSelector(getActiveCreditApplication)

  // State
  const [code, setCode] = useState('')
  const [bankManagerId, setBankManagerId] = useState<string | undefined>(undefined)

  // Queries
  const [fetchBankManagers, { data: bankManagersData, isLoading: isBankManagersLoading, isError: isBankManagerError }] =
    useLazyGetBankManagersQuery()

  // Mutations
  const [attachBankManagerToLead, { isLoading: isAttachManagerLoading, isSuccess }] = useAttachBankManagerMutation()

  // Form
  const { watch, control } = useForm({
    defaultValues: {
      noOneHelped: false
    }
  })

  const noOneHelped = watch('noOneHelped')
  const manager = bankManagersData?.results?.[0]
  const isError = isBankManagerError || bankManagersData?.results.length === 0

  // Handlers
  const handleOtpChange = (code: string) => {
    setCode(code)
    if (code.length === NUM_INPUTS) {
      handleBankManagerCodeComplete(code)
    }
  }

  const handleBankManagerCodeComplete = async (code: string) => {
    try {
      const res = await fetchBankManagers({ search: code }, true).unwrap()
      const manager = res.results?.[0]
      if (!manager) return
      setBankManagerId(manager.id)
    } catch (err) {
      console.log(err)
    }
  }

  const handleAttachBankManager = async (id: string | undefined) => {
    if (!id || !creditID) return
    try {
      await attachBankManagerToLead({
        credit_id: creditID,
        body: {
          bank_manager: id
        }
      }).unwrap()
      onAttached?.()
    } catch (err) {
      console.log(err)
    }
  }

  const onAttach = async () => {
    try {
      await handleAttachBankManager(bankManagerId)
    } catch {
      console.log('err happened')
    }
  }

  const onSkip = async () => {
    try {
      const res = await fetchBankManagers({ search: '0000' }, true).unwrap()
      const manager = res.results?.[0]
      if (!manager) return
      await handleAttachBankManager(manager.id)
    } catch (err) {
      console.log(err)
    }
  }

  if (isSuccess) {
    return (
      <InfoBlock twStyle={[twStyle]}>
        <Typography variant='subbody' twStyle={tw`font-medium`}>
          Спасибо, ваш запрос отправлен
        </Typography>
      </InfoBlock>
    )
  }

  return (
    <InfoBlock isContentLoading={isLoading} twStyle={[tw`relative flex flex-col`, twStyle]}>
      <Alert icon={null} twStyle={tw`px-4`}>
        <Typography variant='subbody'>{t('create-lead-bank-manager-help')}</Typography>
      </Alert>
      <div className='space-y-2 mt-4'>
        <OtpInput
          isDisabled={isBankManagersLoading || noOneHelped}
          numInputs={NUM_INPUTS}
          value={code}
          onChange={handleOtpChange}
          inputStyle={tw`border border-transparent outline-none font-bold bg-input w-[50px] h-[60px] mr-2 rounded-2xl`}
          focusStyle={tw`border-active`}
          hasErrored={isError}
          errorStyle={tw`border-error focus:border-error`}
          disabledStyle={tw`opacity-40`}
        />
        {isError && (
          <Typography variant='caption' twStyle={tw`text-red-dark`}>
            Менеджера с таким кодом нет
          </Typography>
        )}
      </div>
      {manager ? <Typography twStyle={tw`mt-4`}>{manager.full_name}</Typography> : null}
      <Checkbox control={control} name='noOneHelped' disabled={!!manager}>
        <Typography variant='subbody' text>
          Мне никто не помогал
        </Typography>
      </Checkbox>
      {!manager && noOneHelped ? (
        <Button variant='secondary' twStyle={tw`w-min py-3 mt-4 self-end`} onClick={onSkip}>
          Продолжить
        </Button>
      ) : null}
      {!noOneHelped && manager ? (
        <Button
          variant='secondary'
          twStyle={tw`w-min py-3 mt-4 self-end`}
          disabled={!manager}
          loading={isAttachManagerLoading || isBankManagersLoading}
          onClick={onAttach}
        >
          Отправить
        </Button>
      ) : null}
    </InfoBlock>
  )
}

export default BankManagerCode
